/**
 * 이상한 방 - 개봉 전의 시각표 ("언제")
 *
 * 근거 문서: 로드맵 확정안 3.3절(연출 시퀀스) · 0절 판정표 10번 · 6절("근접 실패 색 깜빡" 기각)
 *
 * `envelopePhases.ts`에서 갈라져 나왔다(P4 리뷰). 그 파일은 **전이**(어느 단계에서 어느 단계로, 예약 슬롯 둘)를
 * 쥐고, 표는 여기 혼자 산다 - 릴이 들어오며 지휘자가 400줄을 넘었고 이음매는 처음부터 이 자리였다.
 * "표는 한 파일에만"이라는 규칙(`envelopePhases.test.ts` 1·2번)도 표에 제 이름의 파일이 있는 편이 지키기 쉽다.
 *
 * 짝은 `envelopeCeremony.ts`의 `GRADE_CEREMONY`(개봉 후)다. **개봉 전은 네 등급이 글자 하나까지 같고**
 * 개봉 후는 등급이 정한다 - 그렇게 가른 이유는 그 파일 머리말에 있다.
 *
 * 여기에는 전이도 예약도 DOM도 없다. 표와, 그 표에서 곧바로 나오는 두 값뿐이다.
 */

import { ceremonyFor, ceremonyTotalMs } from './envelopeCeremony'
import type { Grade } from '../game/economy/envelopeTable'

/**
 * 개봉 전의 시각표. **절대 시각(ms)**이다 - 구간 길이가 아니라 "언제"를 적어야 표와 한 줄씩 맞대어 읽힌다.
 *
 * | 시각 | 무엇 |
 * |---|---|
 * | 0~360 | **앞당김.** 봉투가 올라와 자리를 잡고 봉인이 달아오른다. 핀 칩에서 값이 떨어진다. 릴은 아직 안 돈다 |
 * | 360~2960 | **릴.** 트랙이 흐르다 감속해 `gradeRoll`에 멎는다. 봉투가 **점점 빨라지며** 떨리고, 봉인 틈에서 빛이 새고, 똑딱의 간격이 벌어진다 |
 * | 2250~2960 | 마지막 접근. 차오르는 소리가 반음씩 올라 **릴이 멎는 순간에 끝난다**(길이는 `envelopeChargeSec`이 안다) |
 * | 2960~3280 | **착지 + 숨 한 박자 정지.** 마커가 한 번 밝아지고 똑딱 하나가 겹친 뒤(둘 다 등급과 무관하다) 0.32초가 침묵이다. 0.25초는 그 표식이 사그라들기도 전에 카드가 뒤집히는 길이였다 |
 * | 3280~ | 등급이 드러난다. 여기서부터는 `GRADE_CEREMONY`의 몫이다 |
 *
 * 네 등급의 이 구간은 완전히 같다. 값을 바꾸면 `envelope.test.ts`의 시각표 검사가 따라온다.
 * 열 장은 여기에 아홉 장을 넘기는 시간이 더 붙고, 그동안 릴은 제자리에서 계속 돈다(`enterStack`).
 */
export const ENVELOPE_TIMELINE = {
  /** 앞당김이 끝나는 순간. 여기서 릴과 바늘이 출발하고 떨림이 시작된다. */
  riseMs: 360,
  /** 릴과 바늘이 멎는 순간. 여기서부터 정지다. */
  sweepEndMs: 2960,
  /** 정지가 끝나는 순간. 등급이 드러난다. 위 표의 이유로 0.25초에서 0.32초가 됐다. */
  holdEndMs: 3280,
} as const

/** 릴이 도는 시간. 표에 한 벌 더 적지 않고 구간에서 뽑는다 - 그래야 표를 고치면 릴이 따라온다. */
export function reelSpinMs(): number {
  return ENVELOPE_TIMELINE.sweepEndMs - ENVELOPE_TIMELINE.riseMs
}

/** 개봉 전 + 이 등급의 세리머니를 합한 총 길이. "이만큼 지나면 끝"을 묻는 자리가 쓴다. */
export function envelopeDurationMs(grade: Grade, reduced = false): number {
  return ENVELOPE_TIMELINE.holdEndMs + ceremonyTotalMs(ceremonyFor(grade, reduced))
}
